// 261010 Epic 2 / P3e (lượt 2) — "Dựng lại theo diễn xuất" của MỘT video (thuần: không antd/React, tiêm API / đồng hồ để test).
// BE (`dev-be-1.md` L2.4 mục 3): `POST …/acting` (job nền khoá `FILM_DIRECT`) → khi artifact sẵn (`GET …/acting` ⇒ `AI`, `stale:false`) → `POST …/direct-acting` (cũng nền, cùng khoá).
// Hai job cùng khoá nên PHẢI đợi hết việc ghi plan giữa hai bước, nếu không `direct-acting` ăn 409 bận. Không có endpoint "xong chưa" riêng ⇒ hỏi `workflow-status.jobs` (sau mỗi lần NGỦ, để job vừa mở đã hiện).
// Lỗi của POST (409 sửa tay / ACTING_NOT_READY / bận…) ném ra nguyên vẹn cho màn đổi thành chữ (`actingActionError`).
import type { ActingState } from '../../types/filmActing';
import { planWriteKindsOf } from './workflowActionPolicy';

export type RebuildMode = 'REBUILD' | 'DIRECT_ONLY';
export type RebuildPhase = 'ACTING' | 'DIRECTING';
export type RebuildResult = { kind: 'DONE' } | { kind: 'ABORTED' } | { kind: 'ACTING_NOT_WRITTEN' } | { kind: 'TIMEOUT' } | { kind: 'JOBS_UNKNOWN' };
export interface RebuildDeps {
  startActing: () => Promise<unknown>;
  directActing: () => Promise<unknown>;
  getState: () => Promise<ActingState>;
  /** Việc nền đang chạy của plan (`workflow-status.jobs`); `null`/`undefined` = không hỏi được. */
  refreshJobs: () => Promise<Array<{ type: string }> | null | undefined>;
  sleep: (ms: number) => Promise<void>;
  /** Màn còn sống? `false` (rời trang) ⇒ dừng, không gọi bước kế. */
  alive: () => boolean;
  onPhase?: (p: RebuildPhase) => void;
}

export const REBUILD_POLL_MS = 2000;
export const REBUILD_MAX_WAIT_MS = 10 * 60 * 1000;

type Wait = 'IDLE' | 'ABORTED' | 'TIMEOUT' | 'JOBS_UNKNOWN';
async function waitIdle(deps: RebuildDeps, pollMs: number, maxWaitMs: number): Promise<Wait> {
  let waited = 0;
  for (;;) {
    await deps.sleep(pollMs);
    waited += pollMs;
    if (!deps.alive()) return 'ABORTED';
    const jobs = await deps.refreshJobs();
    if (!deps.alive()) return 'ABORTED';
    if (!jobs) return 'JOBS_UNKNOWN';
    if (planWriteKindsOf(jobs).length === 0) return 'IDLE';
    if (waited >= maxWaitMs) return 'TIMEOUT';
  }
}

/** Artifact đủ điều kiện dàn shot: đã viết (`AI`), còn tươi, không bị bảo vệ sửa tay. */
const readyToDirect = (s: ActingState): boolean => s.status === 'AI' && !s.stale && !s.protected;

export async function rebuildByActing(mode: RebuildMode, deps: RebuildDeps, opts: { pollMs?: number; maxWaitMs?: number } = {}): Promise<RebuildResult> {
  const pollMs = opts.pollMs ?? REBUILD_POLL_MS;
  const maxWaitMs = opts.maxWaitMs ?? REBUILD_MAX_WAIT_MS;
  if (mode === 'REBUILD') {
    deps.onPhase?.('ACTING');
    await deps.startActing();
    const w = await waitIdle(deps, pollMs, maxWaitMs);
    if (w !== 'IDLE') return { kind: w };
  }
  if (!deps.alive()) return { kind: 'ABORTED' };
  const state = await deps.getState();
  if (!deps.alive()) return { kind: 'ABORTED' };
  if (!readyToDirect(state)) return { kind: 'ACTING_NOT_WRITTEN' };
  deps.onPhase?.('DIRECTING');
  await deps.directActing();
  const w2 = await waitIdle(deps, pollMs, maxWaitMs);
  return w2 === 'IDLE' ? { kind: 'DONE' } : { kind: w2 };
}
